import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, MapPin, Briefcase, Clock, ArrowUpRight, Loader2 } from "lucide-react";
import { CAREERS } from "../data";
import { publicCareers } from "../lib/api";
import Logo from "../components/vyft/Logo";
import ApplyDialog from "../components/vyft/ApplyDialog";
import { Reveal, Overline } from "../components/vyft/primitives";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const PERKS = ["Senior, hands-on team", "In-house film studio", "Real ownership from day one", "Learning budget", "Hybrid where it fits", "Work with brands you know"];

export default function Careers() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState(null);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    window.scrollTo(0, 0);
    publicCareers()
      .then((data) => setRoles(Array.isArray(data) ? data : []))
      .catch(() => setRoles(CAREERS));
  }, []);

  const list = roles === null ? [] : roles;
  const loading = roles === null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 inset-x-0 z-50 px-4 pt-4">
        <div className="mx-auto max-w-7xl flex items-center justify-between rounded-full px-4 md:px-6 h-16 backdrop-blur-xl bg-background/70 border border-border/10">
          <button data-testid="careers-logo" onClick={() => navigate("/")} className="flex items-center gap-2"><Logo /></button>
          <a href="/#contact" className="hidden sm:inline-flex"><Button className="rounded-full bg-brand-gradient text-white border-0 hover:opacity-90">Contact us</Button></a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 overflow-hidden grain">
        <div className="orb bg-[#FF1F8E] h-96 w-96 -top-32 -left-24" />
        <div className="orb bg-[#F97316] h-80 w-80 top-10 right-0" />
        <div className="relative z-10 mx-auto max-w-5xl">
          <button data-testid="careers-back" onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back home
          </button>
          <div className="mt-8"><Overline>Careers at VYFT</Overline></div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]"
          >
            Build brands that grow.<br /><span className="text-gradient">Build your career.</span>
          </motion.h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            We're a 360° growth studio where designers, marketers, producers and engineers ship
            work that actually moves businesses. If you love craft and outcomes in equal measure, you'll fit right in.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {PERKS.map((p) => (
              <span key={p} className="rounded-full border border-border/15 bg-card px-4 py-1.5 text-xs font-medium">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Openings */}
      <section className="px-6 pb-24">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-3xl font-bold tracking-tight">Open roles</h2>
            <span className="text-sm text-muted-foreground" data-testid="careers-count">
              {loading ? "Loading…" : `${list.length} ${list.length === 1 ? "position" : "positions"}`}
            </span>
          </div>
          {loading && (
            <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          )}
          {!loading && list.length === 0 && (
            <div className="rounded-3xl border border-border/10 bg-card p-10 text-center text-muted-foreground">
              No open roles right now — but we're always keen to meet great people. Use the Open Application below.
            </div>
          )}
          <div className="space-y-4">
            {list.map((role, i) => (
              <Reveal key={role.id} delay={(i % 5) * 0.06}>
                <div data-testid={`role-${role.id}`} className="group rounded-3xl border border-border/10 bg-card p-6 md:p-8 shimmer-card">
                  <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/careers/${role.id}`} data-testid={`role-link-${role.id}`} className="font-display text-xl md:text-2xl font-bold tracking-tight hover:text-gradient transition-colors">{role.title}</Link>
                        <Badge variant="outline" className="border-primary/30 text-primary">{role.dept}</Badge>
                      </div>
                      <p className="mt-2 text-muted-foreground">{role.summary}</p>
                      <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" /> {role.dept}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {role.type}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {role.location}</span>
                      </div>
                      <Link to={`/careers/${role.id}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                        View role <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </div>
                    <ApplyDialog role={role} />
                  </div>
                  <div className="mt-6 grid md:grid-cols-2 gap-6 border-t border-border/10 pt-6">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-primary">What you'll do</div>
                      <ul className="mt-3 space-y-2">
                        {role.responsibilities.map((r) => (
                          <li key={r} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {r}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-primary">What we're looking for</div>
                      <ul className="mt-3 space-y-2">
                        {role.requirements.map((r) => (
                          <li key={r} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {r}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Open application CTA */}
          <div className="mt-12 rounded-[2rem] bg-brand-gradient p-10 md:p-14 text-center">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-white tracking-tight">Don't see your role?</h2>
            <p className="mt-3 text-white/85 max-w-xl mx-auto">We're always keen to meet exceptional people. Send us your work and tell us where you'd make an impact.</p>
            <div className="mt-8 inline-flex"><ApplyDialog role={{ id: "open-application", title: "Open Application" }} light /></div>
          </div>
        </div>
      </section>
    </div>
  );
}
